import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiDownload, FiEye, FiPlus, FiInfo, FiLock, FiClock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

/* ─── Image / Video Modal with Download ────────────────── */
const ImageModal = ({ media, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  if (!media || media.length === 0) return null;

  const goToNext = () =>
    setCurrentIndex((prev) => (prev + 1) % media.length);
  const goToPrev = () =>
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
  const isVideo = (url) =>
    url?.includes(".mp4") || url?.includes(".webm") || url?.includes("video");

  const currentUrl = media[currentIndex];

  const handleDownload = (e) => {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = currentUrl;
    link.download = `order-media-${currentIndex + 1}`;
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-5xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar controls */}
        <div className="absolute -top-12 left-0 right-0 flex items-center justify-between text-white">
          <span className="text-sm font-medium text-white/80">
            Media {currentIndex + 1} of {media.length}
          </span>
          <div className="flex items-center gap-4">
            <button
              onClick={handleDownload}
              title="Download media"
              className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition"
            >
              <FiDownload className="text-sm" /> Download
            </button>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black">
          {isVideo(currentUrl) ? (
            <video
              src={currentUrl}
              controls
              autoPlay
              className="w-full max-h-[75vh] object-contain bg-black"
            />
          ) : (
            <img
              src={currentUrl}
              alt="Media"
              className="w-full max-h-[75vh] object-contain bg-black"
            />
          )}
        </div>

        {media.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#772037] hover:bg-[#CC234F] text-white rounded-full p-3 border border-white/20 transition-all shadow-lg"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={goToNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#772037] hover:bg-[#CC234F] text-white rounded-full p-3 border border-white/20 transition-all shadow-lg"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};



/* ─── Reusable UI Atoms ────────────────────────────────── */
const SectionCard = ({ title, icon, children, className = "" }) => (
  <div
    className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden ${className}`}
  >
    {title && (
      <div className="px-3.5 sm:px-5 py-2.5 sm:py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/70">
        {icon && <span className="text-sm">{icon}</span>}
        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          {title}
        </h3>
      </div>
    )}
    <div className="px-3.5 sm:px-5 py-3 sm:py-4">{children}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    Approved: {
      bg: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-500",
      text: "text-emerald-700",
      border: "border-emerald-200",
    },
    Rejected: {
      bg: "bg-red-50 text-red-700 border-red-200",
      dot: "bg-red-500",
      text: "text-red-700",
      border: "border-red-200",
    },
    Pending: {
      bg: "bg-amber-50 text-amber-700 border-amber-200",
      dot: "bg-amber-500",
      text: "text-amber-700",
      border: "border-amber-200",
    },
  };
  const key = status || "Pending";
  const s = map[key] || map.Pending;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-bold border ${s.bg} ${s.text} ${s.border}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${s.dot} ${key === "pending" ? "animate-pulse" : ""
          }`}
      />
      {status === "Approved"
        ? "Approved by QQT"
        : status === "Rejected"
          ? "Rejected"
          : "Under Review"}
    </span>
  );
};

const Chip = ({ emoji, label, value, colorClass = "bg-gray-100 text-gray-700" }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold ${colorClass}`}
  >
    {emoji && <span>{emoji}</span>}
    {label && <span className="text-gray-400 font-normal">{label}:</span>}
    <span className="font-bold">{value}</span>
  </span>
);

const fmtDays = (ms) => {
  if (!ms) return null;
  const diff = new Date(ms) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

/* ─── Product Hierarchy (Category → SubCat → Product → Variety) ── */
const ProductHierarchy = ({ order }) => {
  const steps = [
    {
      label: "Variety",
      name: order.productVariety?.varietyName,
      img: order.productVariety?.img || order.product?.img,
      badge: order.productVariety?.grade,
      moq: order.productVariety?.slabAdjustedMOQ
        ? `Min. MOQ: ${order.productVariety.slabAdjustedMOQ} ${order.productVariety.standardizedUNIT || ""
        }`
        : null,
      isLast: true,
    },
  ];

  return (
    <div className="w-full">
      <div className="space-y-2">
        {steps.map((step, i) => (
          <div
            key={step.label}
            className={`flex items-center gap-3 rounded-xl border p-2.5 ${step.isLast
                ? "border-[#772037]/25 bg-gradient-to-r from-[#772037]/5 to-transparent"
                : "border-gray-100 bg-white"
              }`}
          >
            <div className="flex flex-col items-center self-stretch mr-1">
              {i > 0 && <div className="w-px flex-1 bg-gray-200 mb-1" />}
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${step.isLast ? "bg-[#772037]" : "bg-gray-300"
                  }`}
              />
              {i < steps.length - 1 && (
                <div className="w-px flex-1 bg-gray-200 mt-1" />
              )}
            </div>

            {/* Image */}
            <div
              className={`w-12 h-12 rounded-xl overflow-hidden border flex-shrink-0 bg-white ${step.isLast ? "border-[#772037]/20" : "border-gray-100"
                }`}
            >
              {step.img ? (
                <img
                  src={step.img}
                  alt={step.name || "Variety"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-50 text-xl">
                  📦
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span
                  className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${step.isLast
                      ? "bg-[#772037] text-white"
                      : "bg-gray-100 text-gray-500"
                    }`}
                >
                  {step.label}
                </span>
                {step.badge && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase">
                    {step.badge}
                  </span>
                )}
              </div>
              <p
                className={`text-sm font-bold mt-0.5 truncate ${step.isLast ? "text-[#772037]" : "text-gray-800"
                  }`}
              >
                {step.name || "—"}
              </p>
              {step.moq && (
                <p className="text-[10px] text-gray-400 mt-0.5">{step.moq}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Main Component ───────────────────────────────────── */
const SupplierOrderDetailCard = ({ order }) => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center p-8">
        <div className="w-16 h-16 rounded-2xl bg-[#772037]/8 flex items-center justify-center mb-4">
          <span className="text-3xl">📋</span>
        </div>
        <p className="text-gray-400 font-medium text-sm">
          Select an order to view details
        </p>
      </div>
    );
  }

  const allMedia = [
    ...(order.referencePhotoAndVideo?.images || []),
    ...(order.referencePhotoAndVideo?.videos || []),
  ];
  const isVideo = (url) =>
    url?.includes(".mp4") || url?.includes(".webm") || url?.includes("video");

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
      : "—";

  const isOffer =
    order?.Source === "Offer" ||
    order?.offer?.isAttached ||
    order?.priceAndQuantity?.fixedPrice?.price != null;

  const isClosed = order.stages === "Closed";

  const getQuotationDisabledReason = (ord) => {
    if (!ord) return "";
    const reviewStatus = ord.review?.status || "Pending";
    const isOrderApproved = reviewStatus === "Approved";
    const isOrderRejected = reviewStatus === "Rejected";
    const isOrderPending = reviewStatus === "Pending";
    const allowedStages = ["Order", "Quotation"];
    const isValidStage = !ord.stages || allowedStages.includes(ord.stages);

    if (ord.stages === "Closed") {
      return "This order has been closed. No new quotations can be created for this order.";
    }
    if (isOrderRejected) {
      return "This order has been rejected by admin. Quotation is not allowed.";
    }
    if (isOrderPending) {
      return "This order is under review. You can create quotation once approved by QQT.";
    }
    if (!isValidStage) {
      return "Quotation adding session has been passed for this order.";
    }
    return "";
  };

  const disabledReason = getQuotationDisabledReason(order);
  const canCreateQuotation = !disabledReason;

  const handleCreateQuotationClick = () => {
    if (!canCreateQuotation) {
      toast.error(disabledReason, {
        duration: 3500,
        icon: "⚠️",
      });
      return;
    }
    navigate(`/create-quotation?orderId=${order._id}`);
  };

  return (
    <div className="h-full overflow-y-auto space-y-3.5 pb-6 pr-0.5">
      {/* ══ 1. HEADER BANNER (Primary Color Background) ═════ */}
      <div className="bg-gradient-to-br shineEffect from-[#772037] to-[#CC234F] rounded-2xl p-3.5 sm:p-5 text-white shadow-lg shadow-[#772037]/20">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-1">
              Order Reference
            </p>
            <h2 className="text-base sm:text-xl font-bold tracking-tight">
              {order.orderNumber || `#${order._id?.slice(-6)}`}
            </h2>
            <p className="text-xs text-white/60 mt-0.5">
              Placed on {fmtDate(order.createdAt)}
            </p>
          </div>

          <div className="flex flex-col sm:items-end gap-2.5">
            <div className="flex items-center gap-1.5 flex-wrap sm:justify-end">
              <StatusBadge status={order.review?.status} />
              {isOffer && (
                <span className="text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-white/20 text-white backdrop-blur-sm border border-white/30">
                  🎯 Offer Order
                </span>
              )}
            </div>

            {/* Action Buttons: on big screen (sm:), right-aligned flex. On mobile (< sm), grid grid-cols-2 row */}
            <div className="grid grid-cols-2 sm:flex sm:items-center sm:justify-end gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() =>
                  navigate(`/supplier-quotations?orderId=${order._id}`, {
                    state: { from: "details" },
                  })
                }
                className="w-full sm:w-auto text-[11px] sm:text-xs font-bold px-2.5 sm:px-3.5 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-white border border-white/30 transition shadow-sm flex items-center justify-center gap-1.5 whitespace-nowrap"
              >
                <FiEye className="text-xs sm:text-sm flex-shrink-0" />
                <span className="truncate">View Quotations ({order.quotationCount || 0})</span>
              </button>

              <button
                type="button"
                disabled={isClosed || !canCreateQuotation}
                onClick={handleCreateQuotationClick}
                className={`w-full sm:w-auto text-[11px] sm:text-xs font-bold px-3 sm:px-4 py-2 rounded-xl transition shadow-md flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  isClosed || !canCreateQuotation
                    ? "bg-gray-200/90 text-gray-500 border border-gray-300 cursor-not-allowed opacity-75"
                    : "bg-white text-[#772037] hover:bg-white/95 cursor-pointer"
                }`}
              >
                {isClosed ? (
                  <FiLock className="text-xs sm:text-sm flex-shrink-0 text-red-600" />
                ) : (
                  <FiPlus className="text-xs sm:text-sm flex-shrink-0" />
                )}
                <span className="truncate">
                  {isClosed
                    ? "Order Closed (Blocked)"
                    : !canCreateQuotation
                    ? "Quotation Unavailable"
                    : "Create Quotation"}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-3 sm:mt-4 flex flex-wrap gap-2">
          {order.stages && (
            <Chip
              emoji="📍"
              value={order.stages.replace(/_/g, " ")}
              colorClass={
                order.stages === "Closed"
                  ? "bg-red-900/40 text-red-200"
                  : "bg-white/15 text-white"
              }
            />
          )}
          {order.quotationCount > 0 && (
            <Chip
              emoji="💬"
              value={`${order.quotationCount} Quotes Submitted`}
              colorClass="bg-white/15 text-white font-semibold"
            />
          )}
        </div>
      </div>

      {/* ══ QUOTATION SESSION OVER / CLOSED / UNDER REVIEW BANNER ══ */}
      {isClosed && (
        <div className="p-3.5 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-2.5 text-red-700">
          <FiLock className="text-base flex-shrink-0 mt-0.5 text-red-600" />
          <div>
            <p className="text-xs font-bold text-red-900">Order Closed</p>
            <p className="text-[11px] text-red-700 mt-0.5 leading-snug">
              This order has been closed. No new quotations can be created for this order.
            </p>
          </div>
        </div>
      )}

      {!isClosed && order.stages && !["Order", "Quotation"].includes(order.stages) && (
        <div className="p-3.5 bg-blue-50/80 border border-blue-200 rounded-2xl flex items-start gap-2.5 text-blue-900">
          <FiInfo className="text-base flex-shrink-0 mt-0.5 text-blue-600" />
          <div>
            <p className="text-xs font-bold text-blue-950">Quotation Session Ended</p>
            <p className="text-[11px] text-blue-800 mt-0.5 leading-snug">
              Quotation adding session has been passed for this order. Buyer has selected a quotation or moved to the next stages.
            </p>
          </div>
        </div>
      )}

      {order.review?.status === "Pending" && (
        <div className="p-3.5 bg-amber-50/80 border border-amber-200 rounded-2xl flex items-start gap-2.5 text-amber-900">
          <FiClock className="text-base flex-shrink-0 mt-0.5 text-amber-600" />
          <div>
            <p className="text-xs font-bold text-amber-950">Order Under Review</p>
            <p className="text-[11px] text-amber-800 mt-0.5 leading-snug">
              This order is currently under review by QQT. Quotations can be submitted once approved.
            </p>
          </div>
        </div>
      )}

      {/* ══ 2. PRODUCT VARIETY CARD ════════════════════════ */}
      <SectionCard title="Product Variety" icon="🏷️">
        <ProductHierarchy order={order} />
      </SectionCard>

      {/* ══ 3. PRICE & QUANTITY ═══════════════════════════ */}
      {order.priceAndQuantity && (
        <SectionCard title="Price & Quantity" icon="💰">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2 bg-gradient-to-br from-[#772037]/6 to-[#CC234F]/6 border border-[#772037]/15 rounded-xl p-4 text-center">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                {isOffer ? "Fixed Offer Price" : "Target Price Range"}
              </p>
              <p className="text-xl font-extrabold text-[#772037]">
                {order.priceAndQuantity.currency || "₹"}{" "}
                {isOffer && order.priceAndQuantity?.fixedPrice?.price != null
                  ? order.priceAndQuantity.fixedPrice.price.toLocaleString()
                  : order.priceAndQuantity?.expectedPriceRange?.min === 0 &&
                    order.priceAndQuantity?.expectedPriceRange?.max === 0
                    ? order.priceAndQuantity?.fixedPrice?.price?.toLocaleString() ||
                    "—"
                    : `${order.priceAndQuantity?.expectedPriceRange?.min?.toLocaleString()} – ${order.priceAndQuantity?.expectedPriceRange?.max?.toLocaleString()}`}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                per{" "}
                {order.priceAndQuantity.standardizedUnit ||
                  order.productVariety?.standardizedUNIT ||
                  "Unit"}
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5 text-center">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                Order Quantity
              </p>
              <p className="text-lg font-extrabold text-gray-800">
                {order.priceAndQuantity.MOQ?.toLocaleString() || "—"}
              </p>
              <p className="text-xs text-gray-400">
                {order.priceAndQuantity.standardizedUnit ||
                  order.productVariety?.standardizedUNIT ||
                  "Units"}
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5 text-center">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                Standard Unit
              </p>
              <p className="text-lg font-extrabold text-gray-800">
                {order.priceAndQuantity.standardizedUnit ||
                  order.productVariety?.standardizedUNIT ||
                  "—"}
              </p>
              <p className="text-xs text-gray-400">Measure</p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ══ 4. DELIVERY WINDOW ════════════════════════════ */}
      {order.delivery && (
        <SectionCard title="Delivery Window" icon="📅">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase">
                Expected From
              </p>
              <p className="text-sm font-extrabold text-gray-800 mt-1">
                {fmtDate(order.delivery.expectedDeliveryStartDate)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase">
                Expected To
              </p>
              <p className="text-sm font-extrabold text-gray-800 mt-1">
                {fmtDate(order.delivery.expectedDeliveryEndDate)}
              </p>
            </div>
            <div className="col-span-2 sm:col-span-1 bg-[#772037]/5 rounded-xl p-3 text-center border border-[#772037]/15">
              <p className="text-[10px] font-bold text-[#772037]/60 uppercase">
                Remaining Window
              </p>
              <p className="text-sm font-extrabold text-[#772037] mt-1">
                {fmtDays(order.delivery.expectedDeliveryEndDate) != null
                  ? `${fmtDays(order.delivery.expectedDeliveryEndDate)} Days`
                  : "—"}
              </p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ══ 5. PACKAGING DETAILS ══════════════════════════ */}
      {order.packaging && (
        <SectionCard title="Packaging Details" icon="📦">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase">
                Size
              </p>
              <p className="text-sm font-extrabold text-gray-800 mt-1">
                {order.packaging.size} {order.packaging.unit}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase">
                Length
              </p>
              <p className="text-sm font-extrabold text-gray-800 mt-1">
                {order.packaging.dimension?.length || 0} cm
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase">
                Breadth
              </p>
              <p className="text-sm font-extrabold text-gray-800 mt-1">
                {order.packaging.dimension?.breadth || 0} cm
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase">
                Height
              </p>
              <p className="text-sm font-extrabold text-gray-800 mt-1">
                {order.packaging.dimension?.height || 0} cm
              </p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ══ 6. SPECIFICATIONS & REQUIREMENTS ══════════════ */}
      {(order.specification?.relatedQuestionsAndAnswers?.length > 0 ||
        order.specification?.otherRequirements?.length > 0 ||
        (Array.isArray(order.specification) &&
          order.specification.length > 0)) && (
          <SectionCard title="Specifications & Requirements" icon="📐">
            {order.specification?.relatedQuestionsAndAnswers?.length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
                  Questions and Answers related to the Product
                </p>
                <div className="space-y-2.5">
                  {order.specification.relatedQuestionsAndAnswers.map(
                    (qa, i) => (
                      <div
                        key={qa._id || i}
                        className="bg-gray-50 border border-gray-100 rounded-xl p-3.5"
                      >
                        <p className="text-xs font-semibold text-gray-500 mb-1.5 leading-snug">
                          Q: {qa.question}
                        </p>
                        <p className="text-sm font-bold text-gray-800">
                          A: {qa.answeredByBuyer || "—"}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {order.specification?.otherRequirements?.length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
                  Other Requirements
                </p>
                <ul className="space-y-2">
                  {order.specification.otherRequirements.map((req, i) => (
                    <li key={req._id || i} className="flex items-start gap-2.5">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#772037] flex-shrink-0" />
                      <span className="text-sm text-gray-700 font-medium">
                        {req.buyersRequirement}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(order.specification) &&
              order.specification.length > 0 && (
                <div className="space-y-2">
                  {order.specification.map((spec, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 rounded-xl bg-gray-50 text-xs"
                    >
                      <span className="text-gray-500 font-medium">
                        {spec.parameter || spec.key || `Spec #${i + 1}`}
                      </span>
                      <span className="font-bold text-gray-800">
                        {spec.value} {spec.unit || ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
          </SectionCard>
        )}

      {/* ══ 7. REFERENCE MEDIA WITH VIEW & DOWNLOAD ════════ */}
      {allMedia.length > 0 && (
        <SectionCard title="Reference Media" icon="🖼️">
          <div className="flex flex-wrap gap-3">
            {allMedia.map((url, i) => (
              <div
                key={i}
                className="group relative w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden border border-gray-200 hover:border-[#772037]/40 transition-all hover:shadow-md bg-gray-100"
              >
                <button
                  type="button"
                  onClick={() => {
                    setModalIndex(i);
                    setModalOpen(true);
                  }}
                  className="w-full h-full block"
                >
                  {isVideo(url) ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-900">
                      <span className="text-3xl">▶️</span>
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={`Reference ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </button>

                {/* Direct Download Button */}
                <a
                  href={url}
                  download={`order-media-${i + 1}`}
                  target="_blank"
                  rel="noreferrer"
                  title="Download Media"
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-lg bg-black/60 hover:bg-[#772037] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FiDownload className="text-xs" />
                </a>

                {/* View/Play tag */}
                <div className="absolute bottom-1.5 left-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <span className="bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-md font-semibold">
                    {isVideo(url) ? "Play" : "View"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ══ 8. ACTION FOOTER (View & Create Quotation) ═════ */}
      <div className="bg-white rounded-2xl border border-gray-200 p-3 sm:p-4 shadow-sm grid grid-cols-2 gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => navigate(`/supplier-quotations?orderId=${order._id}`)}
          className="w-full py-2.5 sm:py-3 px-2 sm:px-4 rounded-xl text-[11px] sm:text-sm font-bold border border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-800 transition flex items-center justify-center gap-1.5 sm:gap-2 shadow-sm"
        >
          <FiEye className="text-sm sm:text-base flex-shrink-0" />
          <span className="truncate">View Quotations ({order.quotationCount || 0})</span>
        </button>

        <button
          type="button"
          onClick={handleCreateQuotationClick}
          className={`w-full py-2.5 sm:py-3 px-2 sm:px-4 rounded-xl text-[11px] sm:text-sm font-bold transition shadow-md flex items-center justify-center gap-1.5 sm:gap-2 ${
            canCreateQuotation
              ? "text-white bg-primaryColor hover:bg-primaryColor/90 shadow-primaryColor/20"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200 cursor-pointer"
          }`}
        >
          <FiPlus className="text-sm sm:text-base flex-shrink-0" />
          <span className="truncate">
            {isClosed
              ? "Order Closed"
              : !canCreateQuotation
              ? "Create Quotation"
              : "Create Quotation"}
          </span>
        </button>
      </div>

      {/* Fullscreen Media Modal */}
      {modalOpen && (
        <ImageModal
          media={allMedia}
          initialIndex={modalIndex}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
};

export default SupplierOrderDetailCard;
